import { BoxPanel } from "screens/styles";
import { FormHelperText, Stack, Typography, useTheme, ListItemText } from "@mui/material";
import { QuestionFormRoot, Select, StyledMenuItem, StyledAnswerArea, StyledListItemText, StyledListItemButton, StyledSecondaryButton, ClearButtonWrapper, StyledIconTextWrapper, StyledIcon, StyledText } from "./style";
import clsx from "clsx";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { urlConverter } from "utils";
import { interpolate } from "components/core";
import { extractQuestions } from "utils/src/extractQuestions";
import { useOnlineBookingApi } from "shell";
import { handleHttpCode, questionTypeEnum } from "assets/constants";
import { updateQuestionAnswerDict } from "utils/src/updateQuestionAnswerDict";
import { QuestionSingleSkeleton } from "./Skeleton/QuestionSingleSkeleton";
import WarningCreature from "assets/icons/creatures/Warning";
import { useDispatch } from "react-redux";
import { resetUserDataNextSteps, resetUserDataNextWidgets, userDataSet } from 'store/userData';
import { questionMaper } from "./utils";

const requiredAddon = () => <span style={{ color: 'var(--mui-palette-red-900)' }}> *</span>;

const SingleSelectionQuestion = forwardRef(({ data, questions, setAnswer, smallScreen, userData, propertyId, activeStep, properties }, ref) => {
    const theme = useTheme();
    const api = useOnlineBookingApi();
    const dispatch = useDispatch();
    let { questionType, questionConfig, answersConfig, questionKey, title, additionalDescription, answers, hintText, buttonClear, isMandatory, question, executeAfterProxyAnswer, executeAfterInternalAnswer } = data || {};
    const isMapToObject = questionConfig?.mapToObjectType === 5
    const errorRef = useRef(null);

    const [selectedValue, setSelectedValue] = useState(questions?.[questionKey] ?? '') 

    const hasAnswerWebhook = executeAfterProxyAnswer || executeAfterInternalAnswer

    const { customerGUID, clientStateData, journeyGUID } = useMemo(() => {
        return {
            customerGUID: userData.customerGUID,
            clientStateData: userData.clientStateData,
            journeyGUID: userData.journeyGUID
        };
    }, [userData]);


    const questionAnswerDict = useMemo(() => extractQuestions(userData), [userData]);

    const isUserDataLoaded = !!((clientStateData && clientStateData?.customerGUID && clientStateData?.journeyGUID) || (userData && userData.journeyGUID));
    const { isLoading: isQuestionsLoading, data: questionsData, error: webHooQuestionkError, refetch } = api.questions.useQuestionQuery(
        clientStateData?.customerGUID || customerGUID,
        clientStateData?.journeyGUID || journeyGUID,
        questionAnswerDict,
        questionType || questionTypeEnum.singleSelection,
        null,
        propertyId,
        clientStateData?.serviceId || userData?.service?.id,
        clientStateData?.serviceTypeId || userData?.serviceType?.id,
        clientStateData?.unitId || userData?.location?.id,
        clientStateData?.appointmentTypeId || userData?.appointmentType?.id,
        clientStateData?.calendarIds,
        clientStateData?.selectedSlot,
        clientStateData?.selectedDate,
        isUserDataLoaded,
        userData,
    )

    const { mutate: answersMutate, isLoading: isAnsweresLoading, error: answerError, reset: resetAnswer  } = api.answers.useAnswerMutation();

    const hasSetInitialNotValidQuestions = useRef(false);
    const hasSetInitialEmptyQuestions = useRef(false);
    const showQuestion = !!(!isQuestionsLoading && questionsData?.showQuestion);
    const parsedData = !!questionsData?.jsonData && questionMaper(JSON.parse(questionsData?.jsonData))
    if (parsedData) {
        questionConfig = parsedData?.questionConfig
        answersConfig = parsedData?.answersConfig
        questionKey = parsedData?.questionKey
        title = parsedData?.title
        additionalDescription = parsedData?.additionalDescription
        answers = parsedData?.answers
        hintText = parsedData?.hintText
        buttonClear = parsedData?.buttonClear
        isMandatory = parsedData?.isMandatory
        question = parsedData?.question
        executeAfterProxyAnswer = parsedData?.executeAfterProxyAnswer
        executeAfterInternalAnswer = parsedData?.executeAfterInternalAnswer
    }


    useEffect(() => {
        if (answerError && errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorRef.current.focus();
        }

    }, [answerError]);

    /*** If the question is invalid and loading is done, set answer as "" with current question key, for set appointmen validation on submit. */
    useEffect(() => {
        if (!showQuestion && !isQuestionsLoading && !webHooQuestionkError) {
            setTimeout(() => {

                setAnswer({ ...questions, [questionKey]: "" });
            });

            hasSetInitialEmptyQuestions.current = true;
        }
    }, [showQuestion, isQuestionsLoading]);

    useEffect(() => {
        const refetchThisQuestionAfterCurrent = userData[`after_property_questions_on_step_${activeStep}`]?.some(item => item?.propertyId === propertyId)
        if (refetchThisQuestionAfterCurrent && !isQuestionsLoading) {
            refetch()
            resetAnswer()
            onChange('')
        }
    }, [userData[`after_property_questions_on_step_${activeStep}`]]);


    /* If there's an error in answering, remove this question's entry from `questions`.   */
    useEffect(() => {
        if (!isAnsweresLoading && answerError) {
            const { [questionKey]: removedProperty, ...restOfQuestions } = questions;
            setAnswer({ ...restOfQuestions });
        }
    }, [answerError, isQuestionsLoading]);

    const onChange = (answerKey) => {
        setSelectedValue(answerKey);
        
        const noAnswerKey = answerKey === '';
        if (noAnswerKey && !questions) return
        const selectedAnswerConfig = answersConfig
            ? answersConfig.find(answer => answer.answerKey === answerKey)
            : answersConfig?.find(answer => answer.answerKey === selectedValue);

        const answersJsonData = JSON.stringify(selectedAnswerConfig);

        const { updatedQuestionAnswerDict, updatedClientStateData } = updateQuestionAnswerDict(clientStateData, questionAnswerDict, questionKey, answerKey)

        if (!!questions) {
            const noPreviousSelectAndEmptySelect = !questions[questionKey] && noAnswerKey
            const previousSelectOrEmptySelect = (questions[questionKey] === answerKey || noAnswerKey) && ((!isMandatory));

            if (noPreviousSelectAndEmptySelect) return;
            if (previousSelectOrEmptySelect) {
                const { [questionKey]: removedProperty, ...restOfQuestions } = questions;
                setAnswer({ ...restOfQuestions })

                setTimeout(() => {
                    dispatch(userDataSet({
                        clientStateData: { questionAnswerDict: { ...restOfQuestions } }
                    }))
                })

                setTimeout(() => {
                    dispatch(userDataSet({
                        [`single_selection_questions_${activeStep}`]: {}
                    }));
                })

                setTimeout(() => {
                    dispatch(resetUserDataNextWidgets({ properties, propertyId }));
                });

                setTimeout(() => {
                    dispatch(resetUserDataNextSteps());
                }, 30);

                return
            }
        }
        if (hasAnswerWebhook && !noAnswerKey) {
            answersMutate(
                {// TODO RC: validate Answere check
                    customerGUID: clientStateData?.customerGUID || customerGUID,
                    journeyGUID: clientStateData?.journeyGUID || journeyGUID,
                    questionAnswerDict: (!!clientStateData?.questionAnswerDict && updatedClientStateData.questionAnswerDict) || updatedQuestionAnswerDict,
                    questionType: questionType || questionTypeEnum.singleSelection,
                    jsonData: answersJsonData || '',
                    propertyId: propertyId,
                    serviceId: clientStateData?.serviceId || userData?.service?.id,
                    serviceTypeId: clientStateData?.serviceTypeId || userData?.serviceType?.id,
                    unitId: clientStateData?.unitId || userData?.location?.id,
                    appointmentTypeId: clientStateData?.appointmentTypeId || userData?.appointmentType?.id,
                    calendarIds: clientStateData?.calendarIds,
                    selectedSlot: clientStateData?.selectedSlot,
                    selectedDate: clientStateData?.selectedDate,
                }
            );
        }
        if (isMapToObject) {
            setTimeout(() => {
                dispatch(userDataSet({ [`questions_with_filter`]: { ...userData.questions_with_filter || {}, [questionKey]: answerKey } }))
            })
        }
        setTimeout(() => {
            dispatch(userDataSet({
                [`single_selection_questions_${activeStep}`]: {
                    ...userData[`single_selection_questions_${activeStep}`],
                    [questionKey]: answerKey
                }
            }));
        })
        setTimeout(() => {
            setAnswer({ ...questions, [questionKey]: answerKey })
        });

        setTimeout(() => {
            dispatch(resetUserDataNextWidgets({ properties, propertyId }));
        });

        setTimeout(() => {
            dispatch(resetUserDataNextSteps());
        }, 30);
    }

    const renderBigSelector = () => {
        if (!answers || answers.length === 0) return null;
        return (
            <Stack direction={smallScreen ? 'column' : 'row'} gap={'20px'}>
                {answers.map(({ answerKey, title, additionalDescription }) => (
                    <StyledListItemButton
                        disabled={isQuestionsLoading}
                        tabIndex={0}
                        title={`${title}\n${additionalDescription}`}
                        className={clsx({ 'selected': answerKey === selectedValue })}
                        key={answerKey}
                        onClick={() => { answerKey !== selectedValue && onChange(answerKey) }}
                    >
                        <StyledListItemText primary={title} secondary={additionalDescription} />
                    </StyledListItemButton>
                ))}
            </Stack>
        )
    }

    const renderSmallSelector = () => {
        const sx = {
            "&.MuiPaper-root": {
                maxWidth: '670px',
                [theme.breakpoints.down('sm')]: { marginRight: '1em' }
            }
        }
        return (
            <Select
                disabled={isQuestionsLoading}
                value={selectedValue}
                onChange={(e) => onChange(e.target.value)}
                className={clsx({ selected: !!selectedValue })}
                SelectProps={{
                    displayEmpty: true,
                    renderValue: (id) => {
                        const value = answers?.find(({ answerKey }) => id === answerKey)?.title || hintText
                        return <div title={value}>{value}</div>
                    },
                    MenuProps: { PaperProps: { sx } }
                }}
            >
                {answers.map(({ answerKey, title, additionalDescription }) => (
                    <StyledMenuItem key={answerKey} value={answerKey}>
                        <ListItemText
                            primary={title}
                            secondary={additionalDescription}
                            title={`${title} ${additionalDescription}`}
                        />
                    </StyledMenuItem>
                ))}
            </Select>
        )
    }

    if (isQuestionsLoading) { return <QuestionSingleSkeleton /> }

    if (webHooQuestionkError) {
        return (
            <>
                <QuestionFormRoot
                    style={
                        {
                            outline: '1px solid',
                            outlineColor: theme.palette.red[700],
                            borderRadius: '10px',
                        }
                    }
                    ref={ref}>
                    <BoxPanel className="BoxShadow">
                        <StyledIconTextWrapper>

                            <StyledIcon>
                                <WarningCreature />
                            </StyledIcon>
                            <StyledText>
                                {
                                    webHooQuestionkError && (
                                        webHooQuestionkError?.errorCode === handleHttpCode.webHook_Error
                                            ? webHooQuestionkError?.webhookResults?.messages
                                                .map((msg, index) => (
                                                    <div key={index}>
                                                        <span style={{ color: theme.palette.red[700] }} > {msg.message}</span>
                                                    </div>
                                                ))
                                            : <span style={{ color: theme.palette.red[700] }} > {webHooQuestionkError?.title || 'General error'} </span>
                                    )
                                }
                            </StyledText>
                        </StyledIconTextWrapper>
                    </BoxPanel>
                </QuestionFormRoot>
            </>
        )
    }

    if (!isQuestionsLoading && !showQuestion) return null

    return (
        <>
            <QuestionFormRoot
                style={answerError
                    ? {
                        outline: '1px solid',
                        outlineColor: theme.palette.red[700],
                        borderRadius: '10px',
                    }
                    : {}
                }
                ref={ref}>
                <BoxPanel className="BoxShadow">
                    <Stack direction={'row'} justifyContent='space-between'>
                        <div>
                            <Typography variant='h2' style={{ color: `${theme.vars.palette.primary.main}` }}>
                                {title}{(isMandatory) && requiredAddon()}
                            </Typography>
                            <FormHelperText>
                                {interpolate(additionalDescription || question?.additionalText || '', urlConverter, true)}
                            </FormHelperText>
                        </div>
                    </Stack>
                    {!isMandatory &&
                        <ClearButtonWrapper>
                            <StyledSecondaryButton disabled={!selectedValue} onClick={() => { 
                                onChange('')
                                resetAnswer()
                            }}>
                                {buttonClear || data?.question?.buttonClear}
                            </StyledSecondaryButton>
                        </ClearButtonWrapper>
                    }
                    <StyledAnswerArea>
                        {
                            answers?.length < 4
                                ? renderBigSelector()
                                : renderSmallSelector()
                        }
                    </StyledAnswerArea>
                </BoxPanel>
            </QuestionFormRoot>

            {
                answerError && (
                    <div ref={errorRef} tabIndex={-1}>
                        {
                    answerError?.errorCode === handleHttpCode.webHook_Error
                        ? answerError?.webhookResults?.messages
                            .map((msg, index) => (
                                <div key={index}>
                                    <span style={{ color: theme.palette.red[700] }} > {msg.message}</span>
                                </div>
                            ))
                        : <span style={{ color: theme.palette.red[700] }} > {answerError?.title || 'General error'} </span>
                        }
                    </div>
                )
            }
        </>
    )
});

export default SingleSelectionQuestion;